import React, { useEffect, useState } from "react";
import { getLocationsByFirm } from "../../Services/Office";
import { getAddresById } from "../../Services/Address";
import { Link } from "react-router-dom";
import { slugifyUrlSegment } from "../../utils/publicProfileUrls";

interface FirmLocation {
  id: string;
  city: string;
  country: string;
  address: string;
  cityState: string;
  zipCode: string;
  officeType: string;
}

interface FirmLocationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  firmId: string;
  firmName: string;
}

const FirmLocationsModal: React.FC<FirmLocationsModalProps> = ({
  isOpen,
  onClose,
  firmId,
  firmName,
}) => {
  const [locations, setLocations] = useState<FirmLocation[]>([]);
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!firmId) {
      return;
    }
    getOfficesData(firmId);
  }, [firmId]);

  const getOfficesData = async (firmId: string) => {
    try {
      const offices = await getLocationsByFirm(firmId);

      const addresses = await Promise.all(
        offices.map((office) => getAddresById(office.address ?? ""))
      );

      const addressMap = new Map(addresses.map((addr) => [addr.id, addr]));

      const formattedOffices: FirmLocation[] = offices.map((office: any) => {
        const address = addressMap.get(office.address);
        return {
          id: office.id || "",
          city: office.cityName || "",
          country: office.countryName || "",
          address: address?.address1 || "",
          cityState: address?.stateName || "",
          zipCode: address?.zipCode || "",
          officeType: office.officeType || "",
        };
      });

      const sortedOffices = formattedOffices.sort((a, b) => {
        if (a.officeType === "Main" && b.officeType !== "Main") {
          return -1;
        }
        if (a.officeType !== "Main" && b.officeType === "Main") {
          return 1;
        }
        return 0;
      });
      setLocations(sortedOffices);
    } catch (error) {
      console.error("Error fetching offices data:", error);
      setLocations([]);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 pt-20 pl-20">
      <div className="bg-white rounded-2xl w-full max-h-[70vh] max-w-[80vw] overflow-hidden shadow-2xl">
        <div className="flex justify-between pt-6 px-6">
          <h2 className="mx-12 w-full text-center text-xl md:text-2xl lg:text-4xl font-bold text-primary">
            {firmName} Member Offices
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg
              className="w-6 h-6 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(70vh-120px)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {locations.map((location) => (
              <Link
                to={`/member/law-firm/${slugifyUrlSegment(firmName)}/${location.id}`}
                key={location.id}
                className="bg-gray-50 rounded-lg fle flex-col gap-2 p-4 md:p-6 hover:shadow-md transition-shadow duration-200"
              >
                <div className="font-bold text-lg md:text-xl text-gray-800 font-roboto">
                  {[location.city, location.country].filter(Boolean).join(", ")}
                </div>
                <div className="text-sm md:text-base text-gray-600 space-y-1">
                  <p className="font-medium">{location.address}</p>
                  <p>
                    {[location.city, location.cityState]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <p className="font-medium">{location.zipCode}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirmLocationsModal;
